import { groupBy } from 'rambda';
import { sortBy } from 'rambda/immutable';
import { type MouseEvent, useMemo, useRef, useState } from 'react';
import { GroupAdminListItem } from '@/components/groups/group-admin-sectioned-list/group-admin-list-item';
import { GroupAdminMenu } from '@/components/groups/group-admin-sectioned-list/group-admin-menu/group-admin-menu';
import useGroupMembers from '@/hooks/groups/use-group-members';
import { useIsDesktop } from '@/hooks/use-user-platform';
import type { GroupMember } from '@/models/group/group-info';
import { useAuthStore } from '@/stores/auth';

interface Props {
  groupId: number;
}

export default function GroupAdminList({ groupId }: Props) {
  const userId = useAuthStore((s) => s.auth?.UserID);
  const ref = useRef<HTMLUListElement>(null);
  const [showFlyout, setShowFlyout] = useState(false);
  const [selected, setSelected] = useState<GroupMember | null>(null);
  const [position, setPosition] = useState<[number, number] | null>(null);
  const { members } = useGroupMembers(groupId);
  const isDesktop = useIsDesktop();

  const groupedMembers = useMemo(
    () =>
      sortBy(
        ([key]) => -Number(key),
        Object.entries(
          groupBy(
            (m) => m.Role.toString(),
            Object.values(members).filter((m) => m.Role !== 0),
          ),
        ),
      ),
    [members],
  );

  const handleOpenFlyout = (m: GroupMember) => (e: MouseEvent) => {
    if (isDesktop && ref.current) {
      const { y, width } = ref.current.getBoundingClientRect();
      setPosition([width - 24, e.clientY - y]);
    } else {
      setShowFlyout(true);
    }
    setSelected(m);
  };

  const handleCloseFlyout = () => {
    setShowFlyout(false);
    setSelected(null);
  };

  return (
    <>
      <ul
        ref={ref}
        className='mx-5 flex flex-col gap-5'>
        {groupedMembers.map(([key, members]) => (
          <li
            key={key}
            className='first:border-b first:pb-5 only:border-none'>
            <ul className='flex flex-col items-stretch gap-4'>
              {members?.map((m) => (
                <li
                  key={m.ID}
                  className='flex items-center gap-2'>
                  <GroupAdminListItem
                    showBadge
                    member={m}
                    canManage={m.UserID !== userId}
                    onSummonContextMenu={handleOpenFlyout(m)}
                  />
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>

      <GroupAdminMenu
        groupId={groupId}
        show={showFlyout}
        position={position}
        selectedMember={selected}
        onClose={handleCloseFlyout}
      />
    </>
  );
}
